import { resolveActionCards } from './full/resolveActionCards';
import { hasPremiumFeature } from '@lub0v/santa-utils';
import { OrganizerInviteShareForm } from './OrganizerInviteShareForm';
import { CloseIcon } from '@src/components/icons/CloseIcon';
import { AddOrganizerForm } from './AddOrganizerForm';
import { OrganizerFace } from '@src/components/faces/OrganizerFace';
import { useOrganizerRename } from './full/OrganizerIdentity';
import { useTranslation } from 'react-i18next';
import type { BoxInfo, CardInfo } from '@lub0v/santa-utils';
import { Typography, Button, showDialog, showToast } from '@src/components';
import { CrownIcon } from '@src/components/icons/CrownIcon';
import { UsersIcon } from '@src/components/icons/UsersIcon';
import { PlusIcon } from '@src/components/icons/PlusIcon';
import { PremiumUpsell } from '@src/components/PremiumUpsell/PremiumUpsell';
import { usePremiumModal } from '@src/components/PremiumUpsell/PremiumModal';
import { useAppSelector } from '@src/state/hooks';
import { useMobileBoxLayout } from './full/BoxLayouts';
import { OrganizerPanelSection } from './full/OrganizerPanelSection';
import './OrganizersPanel.css';

export function OrganizerPanelOrganizers({ info, cards, loadCards, onChanged, embedded = false }: { embedded?: boolean; info: BoxInfo; cards: CardInfo[]; loadCards?: () => Promise<CardInfo[]>; onChanged: () => Promise<void> | void }) {
  const { t } = useTranslation();
  const mobile = useMobileBoxLayout();
  const openPremium = usePremiumModal();
  const premium = hasPremiumFeature(info.box, 'boxMultiOrg');
  const organizers = info.organizers ?? [];
  const own = organizers.find(person => person.isYou);
  const openRename = useOrganizerRename(own ? { ...info, admin: { ...info.admin, username: own.username, picture: own.picture } } : info, onChanged);
  async function openAdd() {
    if (!premium) { openPremium('organizers', undefined, 'add_organizer', 'feature'); return; }
    if (organizers.length >= 3) return;
    const candidates = await resolveActionCards(loadCards ?? cards);
    if (!candidates) return;
    const existing = new Set(organizers.map(person => person.id));
    showDialog({ title: t('organizersPanel.add'), cancel: t('box.cancel'),
      children: <AddOrganizerForm boxKey={info.box.key} cards={candidates.filter(card => !existing.has(card.id) && !card.isYou)} onChanged={onChanged} />,
      testId: 'add-organizer-dialog' });
  }
  function openRemove(person: NonNullable<BoxInfo['organizers']>[number]) {
    showDialog({ title: t('organizersPanel.removeTitle'),
      children: t('organizersPanel.removeBody', { name: person.username }), cancel: t('box.cancel'),
      submit: t('organizersPanel.remove'), submitVariant: 'danger', testId: 'remove-organizer-dialog',
      onSubmit: async () => {
        const response = await fetch(`/api/box/${encodeURIComponent(info.box.key)}/organizers/${person.id}`, { method: 'DELETE', credentials: 'same-origin' });
        if (!response.ok) { showToast(t('organizersPanel.removeFailed')); throw new Error('Organizer removal failed'); }
        await onChanged();
      } });
  }
  const user = useAppSelector((state) => state.session.user);
  const profiles = user?.profiles || [];
  const profile = profiles.find((item) => item.id === user?.id)
    || profiles.find((item) => item.active) || profiles[0];
  const content = (
    <>
      <Typography as="p" role="body/base">{t('organizersPanel.description')}</Typography>
      <Typography as="p" role="body/base">{t(organizers.length >= 3 ? 'organizersPanel.limitReached' : 'organizersPanel.limitHint')}</Typography>
      <div className={`organizers-panel__body${premium ? ' organizers-panel__body--premium' : ''}`}>
        <div className="organizers-panel__people">
          <button type="button" className="organizers-panel__person organizers-panel__self" onClick={openRename} disabled={info.box.isArchived} data-testid="organizers-panel-self">
            <div className="organizers-panel__avatar"><OrganizerFace picture={own?.picture ?? info.admin.picture ?? profile?.picture} index={profile?.id ?? user?.id} /></div>
            <Typography role="label/button-md">{own?.username ?? info.admin.username ?? t('organizersPanel.you')}</Typography>
            <Typography role="body/sm">{t('organizersPanel.you')}</Typography>
          </button>
          {organizers.filter(person => !person.isYou).map(person => (
            <div className="organizers-panel__person organizers-panel__other" key={person.id}>
              <button type="button" className="organizers-panel__person organizers-panel__self"
                disabled={info.box.isArchived || person.claimed !== false}
                onClick={() => showDialog({ title: t('organizersPanel.inviteNew'), cancel: t('box.close'),
                  children: <OrganizerInviteShareForm boxKey={info.box.key} organizer={person} />,
                  testId: 'organizer-share-dialog' })}>
                <div className="organizers-panel__avatar"><OrganizerFace picture={person.picture} index={person.id} /></div>
                <Typography role="label/button-md">{person.username}</Typography>
              </button>
              <Button variant="ghost" size="small" icon={<CloseIcon />} aria-label={t('organizersPanel.remove')}
                className="organizers-panel__remove" disabled={info.box.isArchived} onClick={() => openRemove(person)}
                testId={`organizer-remove-${person.id}`} />
            </div>
          ))}
          {organizers.length < 3 && <div className={`organizers-panel__person${!premium ? ' organizers-panel__person--offer' : ''}`}>
            <button type="button" onClick={openAdd} disabled={info.box.isArchived} className="organizers-panel__add" aria-label={t('organizersPanel.add')} data-testid="organizers-panel-add"><PlusIcon /></button>
            {!premium ? <Button variant="blue" icon={<CrownIcon />} disabled={info.box.isArchived} onClick={openAdd} className="organizers-panel__premium-add" testId="organizers-panel-premium-add">{t('organizersPanel.add')}</Button> :
              <Typography role="body/base">{t('organizersPanel.add')}</Typography>}
          </div>}
        </div>
        {!premium && <PremiumUpsell feature="organizers" compact testId="organizers-premium-panel" />}
      </div>
    </>
  );
  return (
      <OrganizerPanelSection embedded={embedded} gameKey={info.box.key} mobile={mobile} title={
        <span className="organizers-panel__mobile-title">
          <span>{t('organizersPanel.title')}</span>
        </span>
      } icon={<UsersIcon />} id="organizers">
        <div data-testid="organizers-panel">{content}</div>
      </OrganizerPanelSection>
    );
}
